<template>
    <div class="signup-wrapper">
        <div class="top-nav">
            <button class="back-btn" @click="handleBack" v-if="currentStep < 8">
                <svg class="mr-1" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 12H5" stroke="#FF466A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M12 19L5 12L12 5" stroke="#FF466A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                Back
            </button>
            <div v-else></div>
            <!-- Empty div for flex space -->
            <div class="right-nav" v-if="currentStep < 8">
                <span class="nav-text" @click="$router.push('/reset-password')">Forgot password?</span>
                <button class="login-btn" @click="$router.push('/signin')">Log In</button>
            </div>
        </div>

        <!-- Success Screen (Step 8) uses different layout -->
        <div class="success-container" v-if="currentStep === 8">
            <div class="success-graphics">
                <div class="bg-shape shape-1"></div>
                <div class="bg-shape shape-2"></div>
                <div class="bg-shape shape-3"></div>
                <div class="bg-shape shape-4"></div>
                <div class="bg-shape shape-5"></div>

                <div class="app-icon-container">
                    <img src="@/assets/logo_only.png" alt="XigFor Logo" class="app-icon" />
                </div>
            </div>

            <h1 class="brand-name-pink">XigFor</h1>
            <p class="brand-subtitle">Community resale marketplace</p>

            <h2 class="success-title">Your account has been created<br />successfully.</h2>
            <p class="success-subtitle">To continue and get the full experience, please download<br />our app.</p>

            <button class="gradient-btn active mt-auto" @click="$router.push('/')">Log In</button>
        </div>

        <!-- Steps 1-7 Container -->
        <div class="step-container" :class="{ 'step-container-wide': currentStep === 4 }" v-else>
            <div class="progress-wrapper mb-6">
                <div class="svg-progress">
                    <!-- Circumference is approx 2 * pi * 34 = 213.6 -->
                    <svg viewBox="0 0 76 76" class="circular-chart">
                        <path
                            class="circle-bg"
                            d="M38 4
                a 34 34 0 0 1 0 68
                a 34 34 0 0 1 0 -68" />
                        <path
                            class="circle"
                            :stroke-dasharray="`${(currentStep / 7) * 213.6}, 213.6`"
                            d="M38 4
                a 34 34 0 0 1 0 68
                a 34 34 0 0 1 0 -68" />
                    </svg>
                    <div class="step-inner-circle">
                        {{ currentStep }}
                    </div>
                </div>
            </div>

            <!-- Step 1 -->
            <template v-if="currentStep === 1">
                <h2 class="step-title">Hey! Welcome</h2>
                <p class="step-subtitle">Enter your phone number or email to create<br />your account.</p>

                <div class="tabs">
                    <div class="tab" :class="{ active: activeTab === 'email' }" @click="activeTab = 'email'">Email</div>
                    <div class="tab" :class="{ active: activeTab === 'phone' }" @click="activeTab = 'phone'">Phone</div>
                </div>

                <div class="input-container" v-if="activeTab === 'email'" :class="{ 'error-border': errors.step1 && !isEmailValid, 'mb-8': !(errors.step1 && !isEmailValid), 'mb-2': errors.step1 && !isEmailValid }">
                    <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 7.00005L10.2 11.65C11.2667 12.45 12.7333 12.45 13.8 11.65L20 7" stroke="#999999" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        <rect x="3" y="5" width="18" height="14" rx="2" stroke="#999999" stroke-width="2" stroke-linecap="round" />
                    </svg>
                    <input type="email" placeholder="Email" v-model="formData.email" @input="errors.step1 = false" />
                </div>
                <div class="error-message mb-6" v-if="activeTab === 'email' && errors.step1 && !isEmailValid">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" fill="#FF466A" />
                        <rect x="11" y="7" width="2" height="6" rx="1" fill="white" />
                        <rect x="11" y="15" width="2" height="2" rx="1" fill="white" />
                    </svg>
                    <span>Enter a valid email address</span>
                </div>

                <div class="input-container phone-input" v-if="activeTab === 'phone'" :class="{ 'error-border': errors.step1 && !isPhoneValid, 'mb-8': !(errors.step1 && !isPhoneValid), 'mb-2': errors.step1 && !isPhoneValid }">
                    <PhoneAreaCodeSelect v-model="selectedPhoneAreaCode" @error-change="phoneAreaCodeError = $event" />
                    <div class="phone-divider"></div>
                    <input type="tel" placeholder="Phone number" v-model="formData.phone" @input="errors.step1 = false" />
                </div>
                <div v-if="activeTab === 'phone' && phoneAreaCodeError" class="helper-text mb-6">
                    {{ phoneAreaCodeError }}
                </div>
                <div class="error-message mb-6" v-if="activeTab === 'phone' && errors.step1 && !isPhoneValid">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" fill="#FF466A" />
                        <rect x="11" y="7" width="2" height="6" rx="1" fill="white" />
                        <rect x="11" y="15" width="2" height="2" rx="1" fill="white" />
                    </svg>
                    <span>Enter a valid phone number.</span>
                </div>

                <div class="bottom-actions">
                    <button class="primary-btn mb-6" :class="{ active: isStep1Valid }" :disabled="isLoading" @click="nextStep">
                        <span v-if="!isLoading">Send code</span>
                        <div v-else class="loader"></div>
                    </button>

                    <label class="custom-checkbox mb-4">
                        <input type="checkbox" v-model="formData.agreeTerms" />
                        <span class="checkmark"></span>
                        <span class="label-text"> Agree to the <router-link to="/terms-of-service" class="link">"Xigfor User Terms"</router-link> and <router-link to="/privacy-policy" class="link">"Privacy Terms"</router-link> </span>
                    </label>

                    <label class="custom-checkbox">
                        <input type="checkbox" v-model="formData.agreeSMS" />
                        <span class="checkmark"></span>
                        <span class="label-text"> I agree to receive recurring SMS messages from XigFor related to account updates and service notifications. Message and data rates may apply. Reply Stop to cancel. </span>
                    </label>
                </div>
            </template>

            <!-- Step 2 -->
            <template v-if="currentStep === 2">
                <h2 class="step-title">Verification code</h2>
                <p class="step-subtitle">Check your messages for the 6-digit<br />code.</p>

                <div class="code-inputs" :class="{ 'error-code-group': errors.step2 && !isStep2Valid, 'mb-8': !(errors.step2 && !isStep2Valid), 'mb-2': errors.step2 && !isStep2Valid }">
                    <input
                        v-for="(digit, index) in formData.verificationCode"
                        :key="index"
                        type="text"
                        inputmode="numeric"
                        maxlength="1"
                        v-model="formData.verificationCode[index]"
                        ref="codeInputs"
                        @input="onCodeInput($event, index)"
                        @keydown.delete="onCodeDelete($event, index)"
                        @paste="onCodePaste"
                        class="code-box"
                        :class="{ 'error-border': errors.step2 && !isStep2Valid }" />
                </div>
                <div class="error-message mb-6 justify-center" v-if="errors.step2 && !isStep2Valid">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" fill="#FF466A" />
                        <rect x="11" y="7" width="2" height="6" rx="1" fill="white" />
                        <rect x="11" y="15" width="2" height="2" rx="1" fill="white" />
                    </svg>
                    <span>Incorrect code. Please check and try again.</span>
                </div>

                <div class="bottom-actions">
                    <button class="primary-btn" :class="{ active: isStep2Valid }" :disabled="isLoading" @click="nextStep">
                        <span v-if="!isLoading">Verify</span>
                        <div v-else class="loader"></div>
                    </button>
                </div>
            </template>

            <!-- Step 3 -->
            <template v-if="currentStep === 3">
                <h2 class="step-title">Create a password</h2>
                <p class="step-subtitle">Include letters and numbers for stronger<br />security.</p>

                <div class="input-container mb-6" :class="{ 'error-border': errors.step3 && formData.password.length < 6 }">
                    <input :type="showPwd1 ? 'text' : 'password'" placeholder="Password" v-model="formData.password" @input="errors.step3 = false" />
                    <img :src="showPwd1 ? require('@/assets/eye_open.png') : require('@/assets/eye.png')" class="eye-icon ml-auto" @click="showPwd1 = !showPwd1" alt="Toggle password visibility" />
                </div>

                <div class="input-container" :class="{ 'error-border': errors.step3 && formData.password !== formData.rePassword, 'mb-8': !(errors.step3 && !isStep3Valid), 'mb-2': errors.step3 && !isStep3Valid }">
                    <input :type="showPwd2 ? 'text' : 'password'" placeholder="Re-enter password" v-model="formData.rePassword" @input="errors.step3 = false" />
                    <img :src="showPwd2 ? require('@/assets/eye_open.png') : require('@/assets/eye.png')" class="eye-icon ml-auto" @click="showPwd2 = !showPwd2" alt="Toggle password visibility" />
                </div>

                <div class="error-message mb-6" v-if="errors.step3 && !isStep3Valid">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" fill="#FF466A" />
                        <rect x="11" y="7" width="2" height="6" rx="1" fill="white" />
                        <rect x="11" y="15" width="2" height="2" rx="1" fill="white" />
                    </svg>
                    <span>{{ step3ErrorMsg }}</span>
                </div>

                <div class="bottom-actions">
                    <button class="primary-btn" :class="{ active: isStep3Valid }" :disabled="isLoading" @click="nextStep">
                        <span v-if="!isLoading">Continue</span>
                        <div v-else class="loader"></div>
                    </button>
                </div>
            </template>

            <!-- Step 4 -->
            <template v-if="currentStep === 4">
                <h2 class="step-title">Choose your interests</h2>
                <p class="step-subtitle">This helps us personalize your experience.<br />You can change it anytime.</p>

                <div class="tags-container mb-8">
                    <div v-for="interest in interestOptions" :key="interest.id" class="interest-tag" :class="{ selected: formData.interests.includes(interest.id) }" @click="toggleInterest(interest.id)">
                        {{ interest.name }}
                    </div>
                </div>
                <div v-if="interestOptionsError" class="helper-text mb-8">
                    {{ interestOptionsError }}
                </div>

                <div class="bottom-actions bottom-actions-wide">
                    <button class="primary-btn mt-auto" :class="{ active: isStep4Valid }" :disabled="!isStep4Valid || isInterestSubmitting" @click="submitInterestStep">
                        <span v-if="!isInterestSubmitting">Continue</span>
                        <div v-else class="loader"></div>
                    </button>
                </div>
            </template>

            <!-- Step 5: Username -->
            <template v-if="currentStep === 5">
                <h2 class="step-title">Set your username</h2>
                <p class="step-subtitle">Pick a unique name so friends can find<br />you.</p>

                <div class="input-container mb-8">
                    <input type="text" placeholder="Username" v-model="formData.usernameInput" />
                </div>

                <div class="bottom-actions">
                    <button class="primary-btn mb-4" :class="{ active: !!formData.usernameInput }" :disabled="!formData.usernameInput || isLoading" @click="nextStep">
                        <span v-if="!isLoading">Continue</span>
                        <div v-else class="loader"></div>
                    </button>
                    <button class="skip-btn" @click="skipStep">Skip</button>
                </div>
            </template>

            <!-- Step 6: Birthday -->
            <template v-if="currentStep === 6">
                <h2 class="step-title">When's your birthday?</h2>
                <p class="step-subtitle">You must be 13 or older to join.</p>

                <div class="birthday-inputs mb-8">
                    <input type="text" placeholder="Day" class="bday-box" maxlength="2" v-model="formData.bdayDay" />
                    <div class="bday-divider"></div>
                    <input type="text" placeholder="Month" class="bday-box" maxlength="2" v-model="formData.bdayMonth" />
                    <div class="bday-divider"></div>
                    <input type="text" placeholder="Year" class="bday-box" maxlength="4" v-model="formData.bdayYear" />
                </div>

                <div class="bottom-actions">
                    <button class="primary-btn mb-4" :class="{ active: isStep6Valid }" :disabled="!isStep6Valid || isLoading" @click="nextStep">
                        <span v-if="!isLoading">Continue</span>
                        <div v-else class="loader"></div>
                    </button>
                    <button class="skip-btn" @click="skipStep">Skip</button>
                </div>
            </template>

            <!-- Step 7: Gender -->
            <template v-if="currentStep === 7">
                <h2 class="step-title">Choose your gender</h2>
                <p class="step-subtitle">This helps us personalize your experience.<br />You can change it anytime.</p>

                <div class="gender-options mb-8">
                    <label class="gender-option" :class="{ selected: formData.gender === 'Female' }">
                        <span>Female</span>
                        <input type="radio" value="Female" v-model="formData.gender" />
                        <div class="radio-custom"></div>
                    </label>
                    <label class="gender-option" :class="{ selected: formData.gender === 'Male' }">
                        <span>Male</span>
                        <input type="radio" value="Male" v-model="formData.gender" />
                        <div class="radio-custom"></div>
                    </label>
                    <label class="gender-option" :class="{ selected: formData.gender === 'Prefer not to say' }">
                        <span>Prefer not to say</span>
                        <input type="radio" value="Prefer not to say" v-model="formData.gender" />
                        <div class="radio-custom"></div>
                    </label>
                </div>

                <div class="bottom-actions">
                    <button class="gradient-btn mb-4" :class="{ active: !!formData.gender }" :disabled="!formData.gender || isLoading" @click="submitSignUp">
                        <span v-if="!isLoading">Continue</span>
                        <div v-else class="loader"></div>
                    </button>
                    <button class="skip-btn" @click="skipAndSubmit">Skip</button>
                </div>
            </template>
        </div>

        <!-- Toast notifications -->
        <Toast v-if="toast.visible" :message="toast.message" :type="toast.type" @close="toast.visible = false" />
    </div>
</template>

<script>
import Toast from './Toast.vue'
import PhoneAreaCodeSelect from './PhoneAreaCodeSelect.vue'
import { buildApiUrl } from '@/config/api'
import axios from 'axios'

export default {
    name: 'SignUp',
    components: {
        Toast,
        PhoneAreaCodeSelect
    },
    data() {
        return {
            defaultPhoneAreaCode: {
                name: 'United States, Canada',
                code: '+1',
                countryCode: 'US',
                flagEmoji: ''
            },
            currentStep: 1,
            activeTab: 'email',
            formData: {
                email: '',
                phone: '',
                agreeTerms: false,
                agreeSMS: false,
                verificationCode: ['', '', '', '', '', ''],
                password: '',
                rePassword: '',
                interests: [],
                usernameInput: '',
                bdayDay: '',
                bdayMonth: '',
                bdayYear: '',
                gender: ''
            },
            showPwd1: false,
            showPwd2: false,
            isLoading: false,
            isInterestSubmitting: false,
            accountCreated: false,
            inviterId: null,
            verifyTicket: '',
            phoneAreaCodes: [],
            selectedPhoneAreaCode: {
                name: 'United States, Canada',
                code: '+1',
                countryCode: 'US',
                flagEmoji: ''
            },
            showPhoneAreaCodeDropdown: false,
            phoneAreaCodeError: '',
            errors: {
                step1: false,
                step2: false,
                step3: false
            },
            interestOptionsError: '',
            toast: {
                visible: false,
                message: '',
                type: 'info'
            },
            interestOptions: []
        }
    },
    computed: {
        isEmailValid() {
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.formData.email)
        },
        isPhoneValid() {
            return /^\d{10,15}$/.test(this.formData.phone)
        },
        phoneAccount() {
            return `${this.selectedPhoneAreaCode.code}${this.formData.phone}`.replace(/\s+/g, '')
        },
        defaultFlagEmoji() {
            return '🌐'
        },
        isStep1Valid() {
            const hasContact = this.activeTab === 'email' ? this.isEmailValid : this.isPhoneValid
            return hasContact && this.formData.agreeTerms && this.formData.agreeSMS
        },
        isStep2Valid() {
            return this.formData.verificationCode.every(code => code !== '')
        },
        isStep3Valid() {
            return /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d\W_]{8,}$/.test(this.formData.password) && this.formData.password === this.formData.rePassword
        },
        isStep4Valid() {
            return this.formData.interests.length > 0
        },
        step3ErrorMsg() {
            if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d\W_]{8,}$/.test(this.formData.password)) {
                return 'Password must be at least 8 characters and include both letters and numbers.'
            }
            if (this.formData.password !== this.formData.rePassword) return 'Passwords do not match. Please re-enter them.'
            return ''
        },
        isStep6Valid() {
            return this.formData.bdayDay && this.formData.bdayMonth && this.formData.bdayYear.length === 4
        }
    },
    mounted() {
        const inviterId = this.$route.query.inviterId
        if (inviterId) {
            this.inviterId = inviterId
        }
        this.fetchInterestOptions()
    },
    methods: {
        buildIdentityPayload() {
            const payload = {
                identityType: this.activeTab === 'email' ? 'EMAIL' : 'PHONE'
            }

            if (this.activeTab === 'email') {
                payload.email = this.formData.email
            } else {
                payload.phone = this.formData.phone
                payload.phoneCountryCode = this.selectedPhoneAreaCode.code
            }

            return payload
        },
        resolveApiCode(data) {
            return data?.code || data?.data?.code || data?.msgCode || data?.errorCode || ''
        },
        resolveVerifyTicket(data) {
            return data?.data?.verifyTicket || data?.verifyTicket || ''
        },
        getAuthHeaders() {
            const token = localStorage.getItem('token') || ''
            return token ? { Authorization: `Bearer ${token}` } : {}
        },
        async submitProfileStep(path, payload) {
            const response = await fetch(buildApiUrl(path), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.getAuthHeaders()
                },
                body: JSON.stringify(payload)
            })

            let data = {}
            try {
                data = await response.json()
            } catch (error) {
                console.warn('Profile step response is not valid JSON:', error)
            }

            const apiCode = this.resolveApiCode(data)
            const isBusinessSuccess = apiCode === '' || apiCode === 200 || apiCode === '200'

            if (!response.ok || !isBusinessSuccess) {
                throw new Error(data?.message || 'Profile update failed')
            }

            return data
        },
        async fetchInterestOptions() {
            this.interestOptionsError = ''
            try {
                const data = await axios.get(buildApiUrl('/category/list'), {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        ...this.getAuthHeaders()
                    }
                })
                this.interestOptions = data.data.data
                console.log(this.interestOptions)
            } catch (error) {
                console.error('Fetch interests error:', error)
                this.interestOptions = []
                this.interestOptionsError = 'Interest list is unavailable right now.'
            }
        },
        showToast(message, type = 'info') {
            this.toast.message = message
            this.toast.type = type
            this.toast.visible = true
        },
        handleBack() {
            if (this.currentStep > 1 && this.currentStep < 8) {
                this.currentStep--
            } else if (this.currentStep === 1) {
                this.$router.push('/')
            }
        },
        async nextStep() {
            if (this.currentStep === 1) {
                if (!this.isStep1Valid) {
                    this.errors.step1 = true
                    return
                }
                this.errors.step1 = false

                const payload = {
                    ...this.buildIdentityPayload(),
                    scene: 'REGISTER'
                }

                if (this.activeTab === 'email' && !this.formData.email) return
                if (this.activeTab === 'phone' && !this.formData.phone) return

                this.isLoading = true
                try {
                    const response = await fetch(buildApiUrl('/sso/auth-code/send'), {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload)
                    })
                    const data = await response.json()
                    const apiCode = this.resolveApiCode(data)
                    const reasonCode = data?.data?.reasonCode || data?.reasonCode || ''
                    const reasonMessage = data?.data?.reasonMessage || data?.reasonMessage || ''

                    if (apiCode === 205 || apiCode === '205') {
                        this.showToast(data?.message || reasonMessage || 'This account is already registered. Please log in.', 'warning')
                        return
                    }

                    if (reasonCode === 'ACCOUNT_ALREADY_REGISTERED') {
                        this.showToast(reasonMessage || 'This account is already registered', 'warning')
                        return
                    }

                    if (apiCode === 'LOGIN_REQUIRED') {
                        const shouldGoToLogin = window.confirm(data?.message || 'This account already exists. Go to the login page?')
                        if (shouldGoToLogin) {
                            this.$router.push('/signin')
                        }
                        return
                    }

                    if (response.ok) {
                        this.verifyTicket = ''
                        this.showToast('Verification code sent successfully', 'success')
                        this.currentStep++
                        this.focusFirstCodeInput()
                    } else {
                        this.showToast(data.message || 'Failed to send verification code', 'error')
                    }
                } catch (error) {
                    console.error('Send code error:', error)
                    this.showToast('Network error, please try again later', 'error')
                } finally {
                    this.isLoading = false
                }
            } else if (this.currentStep === 2) {
                if (!this.isStep2Valid) {
                    this.errors.step2 = true
                    return
                }
                this.errors.step2 = false

                const payload = {
                    ...this.buildIdentityPayload(),
                    authCode: this.formData.verificationCode.join(''),
                    scene: 'REGISTER'
                }

                this.isLoading = true
                try {
                    const response = await fetch(buildApiUrl('/sso/auth-code/verify'), {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload)
                    })
                    const data = await response.json()

                    if (response.ok) {
                        this.verifyTicket = this.resolveVerifyTicket(data)
                        if (!this.verifyTicket) {
                            this.errors.step2 = true
                            this.showToast('Verification succeeded but verifyTicket is missing.', 'error')
                            return
                        }
                        this.currentStep++
                    } else {
                        this.errors.step2 = true
                        this.showToast(data.message || 'Incorrect code. Please check and try again.', 'error')
                    }
                } catch (error) {
                    console.error('Verify code error:', error)
                    this.errors.step2 = true
                    this.showToast('Network error, please try again later', 'error')
                } finally {
                    this.isLoading = false
                }
            } else if (this.currentStep === 3) {
                if (!this.isStep3Valid) {
                    this.errors.step3 = true
                    return
                }
                this.errors.step3 = false

                if (this.accountCreated) {
                    this.currentStep++
                    return
                }

                if (!this.verifyTicket) {
                    this.showToast('Please complete verification before creating an account.', 'error')
                    return
                }

                this.isLoading = true
                try {
                    const payload = {
                        ...this.buildIdentityPayload(),
                        password: this.formData.password,
                        verifyTicket: this.verifyTicket
                    }

                    const response = await fetch(buildApiUrl('/sso/register/create-account'), {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload)
                    })

                    const data = await response.json()

                    if (response.ok) {
                        const token = data?.data?.token || data?.data?.accessToken || data?.token || data?.accessToken || ''
                        if (token) {
                            localStorage.setItem('token', token)
                        }
                        this.accountCreated = true
                        await this.fetchInterestOptions()
                        this.currentStep++
                    } else {
                        this.showToast(data.message || 'Registration failed, please try again later', 'error')
                    }
                } catch (error) {
                    console.error('Create account error:', error)
                    this.showToast('Network error, please try again later', 'error')
                } finally {
                    this.isLoading = false
                }
            } else if (this.currentStep < 7) {
                if (this.currentStep === 5) {
                    if (!this.formData.usernameInput || this.isLoading) return

                    this.isLoading = true
                    try {
                        await this.submitProfileStep('/sso/register/profile/nickname', {
                            nickname: this.formData.usernameInput
                        })
                        this.currentStep++
                    } catch (error) {
                        console.error('Submit nickname error:', error)
                        this.showToast(error.message || 'Failed to save nickname', 'error')
                    } finally {
                        this.isLoading = false
                    }
                    return
                }

                if (this.currentStep === 6) {
                    if (!this.isStep6Valid || this.isLoading) return

                    this.isLoading = true
                    try {
                        await this.submitProfileStep('/sso/register/profile/birthday', {
                            birthday: `${this.formData.bdayYear}-${String(this.formData.bdayMonth).padStart(2, '0')}-${String(this.formData.bdayDay).padStart(2, '0')}`
                        })
                        this.currentStep++
                    } catch (error) {
                        console.error('Submit birthday error:', error)
                        this.showToast(error.message || 'Failed to save birthday', 'error')
                    } finally {
                        this.isLoading = false
                    }
                    return
                }

                this.currentStep++
            }
        },
        skipStep() {
            if (this.currentStep < 7) {
                this.currentStep++
            }
        },
        skipAndSubmit() {
            this.formData.gender = '' // Clear if skipped
            this.submitSignUp()
        },
        focusFirstCodeInput() {
            this.$nextTick(() => {
                if (this.$refs.codeInputs && this.$refs.codeInputs.length > 0) {
                    this.$refs.codeInputs[0].focus()
                }
            })
        },
        onCodeInput(event, index) {
            const value = event.target.value
            const numValue = value.replace(/[^0-9]/g, '')
            const singleChar = numValue.charAt(numValue.length - 1) || ''

            this.$set(this.formData.verificationCode, index, singleChar)
            this.errors.step2 = false

            if (singleChar && index < 5) {
                this.$nextTick(() => {
                    this.$refs.codeInputs[index + 1].focus()
                })
            }
        },
        onCodeDelete(event, index) {
            if (!this.formData.verificationCode[index] && index > 0) {
                this.$nextTick(() => {
                    this.$refs.codeInputs[index - 1].focus()
                })
            }
        },
        onCodePaste(event) {
            event.preventDefault()
            const pastedText = (event.clipboardData || window.clipboardData).getData('text')
            const numbers = pastedText
                .replace(/[^0-9]/g, '')
                .slice(0, 6)
                .split('')

            for (let i = 0; i < numbers.length; i++) {
                this.$set(this.formData.verificationCode, i, numbers[i])
            }

            if (numbers.length > 0) {
                const focusIndex = Math.min(numbers.length, 5)
                this.$nextTick(() => {
                    this.$refs.codeInputs[focusIndex].focus()
                })
            }
        },
        toggleInterest(interestId) {
            const idx = this.formData.interests.indexOf(interestId)
            if (idx > -1) {
                this.formData.interests.splice(idx, 1)
            } else {
                this.formData.interests.push(interestId)
            }
        },
        async submitInterestStep() {
            if (!this.isStep4Valid || this.isInterestSubmitting) return

            this.isInterestSubmitting = true
            try {
                const response = await fetch(buildApiUrl('/sso/register/profile/interest'), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...this.getAuthHeaders()
                    },
                    body: JSON.stringify({
                        categoryIds: this.formData.interests
                    })
                })
                const data = await response.json()

                if (response.ok) {
                    this.currentStep++
                } else {
                    this.showToast(data.message || 'Failed to save interests', 'error')
                }
            } catch (error) {
                console.error('Submit interests error:', error)
                this.showToast('Network error, please try again later', 'error')
            } finally {
                this.isInterestSubmitting = false
            }
        },
        async submitSignUp() {
            if (this.isLoading) return
            if (!this.accountCreated) {
                this.showToast('Please create your account before finishing registration.', 'error')
                return
            }

            this.isLoading = true
            try {
                await this.submitProfileStep('/sso/register/profile/gender', {
                    gender: this.formData.gender === 'Male' ? '1' : this.formData.gender === 'Female' ? '2' : '0'
                })
                this.currentStep = 8
            } catch (error) {
                console.error('Submit gender error:', error)
                this.showToast(error.message || 'Failed to save gender', 'error')
            } finally {
                this.isLoading = false
            }
        }
    }
}
</script>

<style scoped>
.mb-4 {
    margin-bottom: 16px;
}
.mb-6 {
    margin-bottom: 24px;
}
.mb-8 {
    margin-bottom: 32px;
}
.mr-1 {
    margin-right: 4px;
}
.ml-auto {
    margin-left: auto;
}
.mt-auto {
    margin-top: auto;
}

.signup-wrapper {
    background-color: #fff;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
}

@media (min-width: 768px) {
    .signup-wrapper {
        background: #fff url('~@/assets/register_bg.png') no-repeat right center;
        background-size: cover;
    }

    .step-container.step-container-wide {
        max-width: 680px;
    }

    .bottom-actions.bottom-actions-wide {
        max-width: 680px;
    }
}

.bottom-actions {
    width: 100%;
    max-width: 450px;
    position: fixed;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    padding: 16px 24px 24px;
    z-index: 10;
    display: flex;
    flex-direction: column;
    align-items: center;
    background: transparent;
    transition: max-width 0.3s;
}

@media (max-width: 767px) {
    .bottom-actions {
        background: #fff;
    }
}

.top-nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
}

.back-btn {
    display: flex;
    align-items: center;
    color: #ff466a;
    font-size: 16px;
    font-weight: 500;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
}

.right-nav {
    display: flex;
    align-items: center;
    font-size: 13px;
}

.nav-text {
    color: rgba(255, 70, 106, 1);
    font-size: 12px;
    font-weight: 500;
    margin-right: 12px;
    cursor: pointer;
}

.login-btn {
    background-color: #ff466a;
    color: white;
    border-radius: 6px;
    padding: 8px 16px;
    font-weight: 600;
    border: none;
    cursor: pointer;
    font-size: 14px;
}

.step-container,
.success-container {
    flex: 1;
    max-width: 450px;
    margin: 0 auto;
    width: 100%;
    padding: 20px 24px 180px;
    display: flex;
    flex-direction: column;
    align-items: center;
    transition: max-width 0.3s;
}

/* SVG Progress Circle */
.progress-wrapper {
    position: relative;
    width: 76px;
    height: 76px;
}

.svg-progress {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
}

.circular-chart {
    display: block;
    margin: 0 auto;
    max-width: 100%;
    max-height: 100%;
}

.circle-bg {
    fill: none;
    stroke: #fceef0;
    stroke-width: 2.5;
}

.circle {
    fill: none;
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke: #ff466a;
    transition: stroke-dasharray 0.3s ease;
}

.step-inner-circle {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 54px;
    height: 54px;
    background-color: #ff466a;
    color: white;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 22px;
    font-weight: bold;
}

.step-title {
    font-size: 28px;
    font-weight: 800;
    color: #333;
    margin-top: 12px;
    margin-bottom: 12px;
    text-align: center;
}

.step-subtitle {
    font-size: 15px;
    color: #111;
    text-align: center;
    margin-bottom: 30px;
    line-height: 1.6;
}

.tabs {
    display: flex;
    width: 100%;
    margin-bottom: 24px;
    border-bottom: 1px solid #eaeaea;
}

.tab {
    flex: 1;
    text-align: center;
    padding: 12px 0;
    font-size: 16px;
    color: #999;
    cursor: pointer;
    position: relative;
    font-weight: 500;
}

.tab.active {
    color: #333;
}

.tab.active::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: #333;
}

/* Error styling */
.error-border {
    border-color: #ff466a !important;
}
.error-message {
    display: flex;
    align-items: center;
    color: #ff466a;
    font-size: 13px;
    width: 100%;
    margin-top: 8px;
}
.error-message.justify-center {
    justify-content: center;
}
.error-message svg {
    margin-right: 6px;
    flex-shrink: 0;
}
.helper-text {
    color: #999;
    font-size: 12px;
    width: 100%;
    margin-top: 8px;
}

.input-container {
    display: flex;
    align-items: center;
    width: 100%;
    border: 1px solid #eaeaea;
    border-radius: 10px;
    padding: 0 16px;
    height: 52px;
    transition: border-color 0.3s;
    background: #fff;
}

.input-container:focus-within {
    border-color: #333;
}

.input-icon {
    margin-right: 12px;
}

.phone-divider {
    width: 1px;
    height: 20px;
    background-color: #eaeaea;
    margin: 0 12px;
}

.input-container input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 15px;
    width: 100%;
    height: 100%;
    background: transparent;
    color: #333;
}

.input-container input::placeholder {
    color: #bbb;
}

.primary-btn,
.gradient-btn {
    width: 100%;
    height: 54px;
    border-radius: 12px;
    background-color: #efefef; /* Default disabed state */
    color: #fff;
    font-size: 16px;
    font-weight: bold;
    border: none;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: all 0.3s;
}

/* Specific active states */
.primary-btn.active {
    background: linear-gradient(270deg, rgba(255, 158, 79, 1) 0%, rgba(255, 66, 107, 1) 100%), linear-gradient(180deg, rgba(255, 158, 79, 1) 0%, rgba(255, 66, 107, 1) 100%), rgba(255, 70, 106, 1);
    box-shadow: 0px 1px 2px rgba(105, 81, 255, 0.05);
}

.gradient-btn.active {
    /* background: linear-gradient(90deg, #ff466a 0%, #ffa86a 100%); */
    background: linear-gradient(270deg, rgba(255, 158, 79, 1) 0%, rgba(255, 66, 107, 1) 100%), linear-gradient(180deg, rgba(255, 158, 79, 1) 0%, rgba(255, 66, 107, 1) 100%), rgba(255, 70, 106, 1);
    box-shadow: 0px 1px 2px rgba(105, 81, 255, 0.05);
}

.skip-btn {
    background: none;
    border: none;
    color: #888;
    font-size: 15px;
    font-weight: 500;
    padding: 8px 16px;
    cursor: pointer;
}

.custom-checkbox {
    display: flex;
    align-items: flex-start;
    cursor: pointer;
    position: relative;
    width: 100%;
}

.custom-checkbox input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
}

.checkmark {
    width: 20px;
    height: 20px;
    border: 1px solid #999;
    border-radius: 50%;
    margin-right: 12px;
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 1px;
    transition: all 0.2s;
}

.custom-checkbox input:checked ~ .checkmark {
    background-color: #ff466a;
    border-color: #ff466a;
}

.custom-checkbox input:checked ~ .checkmark::after {
    content: '';
    width: 8px;
    height: 8px;
    background: white;
    border-radius: 50%;
}

.label-text {
    font-size: 12px;
    color: #999;
    line-height: 1.5;
}

.link {
    color: #ff466a;
    text-decoration: none;
}

.code-inputs {
    display: flex;
    justify-content: center;
    width: 100%;
    gap: 12px;
}

.code-box {
    width: 48px;
    height: 56px;
    border: 1px solid #eaeaea;
    border-radius: 10px;
    font-size: 26px;
    font-weight: bold;
    text-align: center;
    outline: none;
    transition: border-color 0.3s;
    background: #fff;
    color: #333;
}

.code-box:focus {
    border-color: #333;
}

.eye-icon {
    width: 18px;
    height: 18px;
    opacity: 0.6;
    cursor: pointer;
    object-fit: contain;

    transition: opacity 0.2s;
}

.eye-icon:hover {
    opacity: 1;
}

.tags-container {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: center;
    width: 100%;
    padding-bottom: 32px;
}

.interest-tag {
    padding: 10px 18px;
    background-color: #f8f8f8;
    border-radius: 24px;
    font-size: 13px;
    color: #333;
    cursor: pointer;
    transition: all 0.3s;
    font-weight: 500;
}

.interest-tag.selected {
    background-color: #ff466a;
    color: white;
}

/* Birthday Styling */
.birthday-inputs {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    position: relative;
    gap: 0;
}

.bday-divider {
    width: 12px;
    height: 1px;
    background-color: #eaeaea;
    margin: 0 8px;
    flex-shrink: 0;
}

.bday-box {
    flex: 1;
    min-width: 0; /* Prevents input from overflowing */
    height: 52px;
    border: 1px solid #eaeaea;
    border-radius: 8px;
    font-size: 15px;
    font-weight: bold;
    text-align: center;
    color: #333;
    outline: none;
    background: #fff;
}

.bday-box::placeholder {
    color: #bbb;
    font-weight: normal;
}

.bday-box:focus {
    border-color: #333;
}

/* Gender Options */
.gender-options {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
}

.gender-option {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: 56px;
    padding: 0 20px;
    border: 1px solid #eaeaea;
    border-radius: 12px;
    cursor: pointer;
    font-size: 15px;
    color: #333;
    transition: border-color 0.2s;
    background: #fff;
}

.gender-option input {
    display: none;
}

.radio-custom {
    width: 20px;
    height: 20px;
    border: 1px solid #ccc;
    border-radius: 50%;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
}

.gender-option.selected {
    border-color: #ff466a;
}

.gender-option.selected .radio-custom {
    border-color: #ff466a;
    background-color: #ff466a;
}

.gender-option.selected .radio-custom::after {
    content: '';
    width: 8px;
    height: 8px;
    background-color: white;
    border-radius: 50%;
}

/* Success Screen Styling */
.success-graphics {
    position: relative;
    width: 100%;
    height: 250px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 24px;
}

.bg-shape {
    position: absolute;
    border-radius: 20px;
    background-color: #fff0f2;
    z-index: 0;
}

.shape-1 {
    left: 10%;
    top: 20%;
    width: 60px;
    height: 60px;
    border-radius: 16px;
}
.shape-2 {
    left: 25%;
    top: 35%;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background-color: #ffd6dc;
}
.shape-3 {
    right: 5%;
    top: 10%;
    width: 120px;
    height: 120px;
}
.shape-4 {
    left: 15%;
    bottom: 10%;
    width: 80px;
    height: 80px;
}
.shape-5 {
    right: 28%;
    bottom: 25%;
    width: 15px;
    height: 15px;
    border-radius: 50%;
    background-color: #ffd6dc;
}

.app-icon-container {
    position: relative;
    z-index: 2;
    width: 100px;
    height: 100px;
    border-radius: 24px;
    background: white;
    box-shadow: 0 10px 30px rgba(255, 70, 106, 0.2);
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    padding: 8px;
    border: 2px solid white;
}

.app-icon {
    width: 100%;
    height: 100%;
    border-radius: 16px;
    object-fit: contain;
}

.brand-name-pink {
    font-size: 28px;
    font-weight: 800;
    color: #ff466a;
    margin: 0 0 8px 0;
}

.brand-subtitle {
    font-size: 15px;
    color: #888;
    margin-bottom: 40px;
}

.success-title {
    font-size: 24px;
    font-weight: bold;
    color: #333;
    text-align: center;
    margin-bottom: 16px;
    line-height: 1.4;
}

.success-subtitle {
    font-size: 14px;
    color: #888;
    text-align: center;
    line-height: 1.5;
    margin-bottom: 40px;
}

/* Loader CSS */
.loader {
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top: 3px solid #fff;
    width: 24px;
    height: 24px;
    -webkit-animation: spin 1s linear infinite; /* Safari */
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

@media (max-width: 480px) {
    .step-container,
    .success-container {
        padding: 20px 16px 160px;
    }

    .code-box {
        width: 44px;
        height: 52px;
        font-size: 22px;
    }
}
</style>
